/**
 * Video Autoplay Module
 * Simple autoplay and loop for videos in containers with video-autoplay-muted class
 */

export default function VideoAutoplay() {

    function isElementInViewport(el) {
        const rect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    function setupVideoAutoplay() {
        // HTML5 videos - set attributes directly
        const videos = document.querySelectorAll('.video-autoplay-muted video');

        videos.forEach((video) => {
            if (!video.hasAttribute('autoplay')) {
                // Set all required attributes for autoplay
                video.setAttribute('autoplay', '');
                video.setAttribute('muted', '');
                video.setAttribute('loop', '');
                video.setAttribute('playsinline', '');
                video.setAttribute('controls', 'false');

                // Ensure volume is 0
                video.volume = 0;
                video.muted = true;

                // Try to play immediately if in viewport
                if (isElementInViewport(video)) {
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise.then(() => {
                            console.log('Video autoplay successful');
                        }).catch(error => {
                            console.warn('Video autoplay failed:', error);
                            // If autoplay fails, try again after user interaction
                            const tryPlay = () => {
                                video.play().catch(() => {});
                                document.removeEventListener('click', tryPlay);
                                document.removeEventListener('scroll', tryPlay);
                                document.removeEventListener('touchstart', tryPlay);
                            };
                            document.addEventListener('click', tryPlay, { once: true });
                            document.addEventListener('scroll', tryPlay, { once: true });
                            document.addEventListener('touchstart', tryPlay, { once: true });
                        });
                    }
                }
            }
        });

        // YouTube & Vimeo iframes - modify src only if needed
        const iframes = document.querySelectorAll('.video-autoplay-muted iframe');

        iframes.forEach((iframe) => {
            const src = iframe.getAttribute('src');

            if (src && (src.includes('youtube.com') || src.includes('youtu.be'))) {
                if (!src.includes('autoplay=1')) {
                    const videoId = src.match(/(?:embed\/|watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
                    if (videoId) {
                        // Force reload with comprehensive autoplay parameters
                        const newSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1`;
                        iframe.setAttribute('src', newSrc);
                    }
                }
            } else if (src && src.includes('vimeo.com') && !src.includes('autoplay=1')) {
                const videoId = src.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
                if (videoId) {
                    // Force reload with autoplay parameters
                    const newSrc = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&controls=0&background=1`;
                    iframe.setAttribute('src', newSrc);
                }
            }
        });
    }

    function forceVideoStart() {
        // Try to start videos that might have been blocked
        const videos = document.querySelectorAll('.video-autoplay-muted video');

        videos.forEach((video) => {
            if (video.paused && isElementInViewport(video)) {
                video.muted = true;
                video.volume = 0;
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise.catch(() => {
                        console.warn('Force video start failed for:', video);
                    });
                }
            }
        });
    }

    function handleVisibilityChange() {
        if (!document.hidden) {
            setTimeout(() => {
                setupVideoAutoplay();
                forceVideoStart();
            }, 200);
        }
    }

    function handleUserInteraction() {
        setupVideoAutoplay();
        forceVideoStart();
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupVideoAutoplay);
    } else {
        setupVideoAutoplay();
    }

    // Retry after a short delay to ensure iframes are loaded
    setTimeout(() => {
        setupVideoAutoplay();
        forceVideoStart();
    }, 500);

    // Additional retry after page is fully loaded
    window.addEventListener('load', () => {
        setTimeout(() => {
            setupVideoAutoplay();
            forceVideoStart();
        }, 1000);
    });

    // Also run when page becomes visible
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Try to start videos on first user interaction
    const interactionEvents = ['click', 'scroll', 'touchstart', 'mouseenter', 'keydown'];
    const handleFirstInteraction = () => {
        handleUserInteraction();
        // Remove all listeners after first interaction
        interactionEvents.forEach(event => {
            document.removeEventListener(event, handleFirstInteraction);
        });
    };

    interactionEvents.forEach(event => {
        document.addEventListener(event, handleFirstInteraction, { once: true, passive: true });
    });

    // Intersection Observer for better viewport detection
    if (window.IntersectionObserver) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const video = entry.target.querySelector('video');
                    if (video && video.paused) {
                        video.muted = true;
                        video.play().catch(() => {});
                    }
                }
            });
        }, { threshold: 0.5 });

        // Observe all video containers
        const containers = document.querySelectorAll('.video-autoplay-muted');
        containers.forEach(container => observer.observe(container));
    }
}
